import { useCallback, useEffect, useState } from "react";

/**
 * Previous / next paging over a forward-only cursor.
 *
 * The replica pages with DynamoDB's `LastEvaluatedKey`, which only ever points
 * forward, so "previous" is the stack of cursors already followed rather than a
 * computed offset. That is also why there is no page count: the replica cannot
 * say how many pages there are without reading them all.
 */
export interface CursorWalk {
  /** Which list this walk belongs to. */
  readonly key: string;
  /** Cursors followed so far; the last is the page on screen. Empty on page one. */
  readonly cursors: readonly string[];
  /** Page one's cursor, once it has skipped past empty pages; absent, page one is the list's start. */
  readonly first?: string;
}

export const startWalk = (key: string): CursorWalk => ({ key, cursors: [] });

export const advance = (walk: CursorWalk, cursor: string): CursorWalk => ({
  ...walk,
  cursors: [...walk.cursors, cursor],
});

export const retreat = (walk: CursorWalk): CursorWalk => ({ ...walk, cursors: walk.cursors.slice(0, -1) });

/**
 * Requests the page on screen with `cursor` instead, without counting a page:
 * the page it replaces came back empty, so there is nothing to go back to.
 */
export const skipPast = (walk: CursorWalk, cursor: string): CursorWalk =>
  walk.cursors.length === 0 ? { ...walk, first: cursor } : { ...walk, cursors: [...walk.cursors.slice(0, -1), cursor] };

export const currentCursor = (walk: CursorWalk): string | undefined =>
  walk.cursors[walk.cursors.length - 1] ?? walk.first;

export const pageNumber = (walk: CursorWalk): number => walk.cursors.length + 1;

/**
 * The walk for `key`: the existing one if it is for the same list, otherwise a
 * fresh one. A cursor is only meaningful to the query that issued it, so one
 * list's cursor must never reach another list's query.
 */
export const walkFor = (walk: CursorWalk, key: string): CursorWalk => (walk.key === key ? walk : startWalk(key));

export interface CursorPager {
  /** The cursor to request the page on screen with; `undefined` on page one. */
  readonly cursor: string | undefined;
  /** 1-based. */
  readonly page: number;
  readonly canGoBack: boolean;
  /** Move to the page `cursor` points at — the cursor the current page's response returned. */
  readonly next: (cursor: string) => void;
  readonly back: () => void;
  /** Request the page on screen with `cursor` instead — see {@link skipPast}. */
  readonly skip: (cursor: string) => void;
}

/**
 * @param key - Identity of the list being paged. Changing it starts again from
 *   page one, so a filter change cannot resume mid-way through a different list.
 */
export function useCursorPager(key: string): CursorPager {
  const [stored, setStored] = useState(() => startWalk(key));

  // Adjusted during render rather than in an effect, so the query rendered
  // alongside never sees the outgoing list's cursor, not even for one frame.
  const walk = walkFor(stored, key);
  if (walk !== stored) setStored(walk);

  const next = useCallback((cursor: string) => setStored((prev) => advance(walkFor(prev, key), cursor)), [key]);
  const back = useCallback(() => setStored((prev) => retreat(walkFor(prev, key))), [key]);
  const skip = useCallback((cursor: string) => setStored((prev) => skipPast(walkFor(prev, key), cursor)), [key]);

  return {
    cursor: currentCursor(walk),
    page: pageNumber(walk),
    canGoBack: walk.cursors.length > 0,
    next,
    back,
    skip,
  };
}

/**
 * Follows the cursor past pages that came back empty with more behind them.
 *
 * The fleet axis reads a bounded number of month buckets per request, so a
 * quiet stretch — the empty months at the start of an oldest-first window —
 * answers with no rows and a cursor. Showing that page would leave the viewer
 * paging through blanks to reach the first row.
 *
 * @returns Whether the page on screen is one being skipped — show it as loading.
 */
export function useSkipEmptyPages(
  pager: CursorPager,
  page: { readonly items: readonly unknown[]; readonly cursor: string | null } | undefined,
  isPlaceholderData: boolean,
): boolean {
  const passThrough = page !== undefined && !isPlaceholderData && page.items.length === 0 ? page.cursor : null;
  const { skip } = pager;
  useEffect(() => {
    if (passThrough !== null) skip(passThrough);
  }, [passThrough, skip]);
  // Held over while the next page loads, an empty page is the one being skipped.
  return page !== undefined && page.items.length === 0 && (passThrough !== null || isPlaceholderData);
}
